import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { adminAPI } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, BarChart3, Shield, LayoutGrid, Activity,
  Search, Ban, CheckCircle2, Star, StarOff, Trash2, KeyRound,
  TrendingUp, Gamepad2, Trophy, Swords, UserX, UserCheck,
  ArrowUpDown, ArrowUp, ArrowDown, FileText, Mail
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

// Lazy import BlogTab
import BlogTab from './BlogTab';
import EmailTab from './EmailTab';

const TABS = [
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'community', label: 'Community', icon: LayoutGrid },
  { id: 'blog', label: 'Blog', icon: FileText },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'health', label: 'System Health', icon: Activity },
];

// ==================== ANALYTICS TAB ====================
function AnalyticsTab({ onFilterUsers }) {
  useSEO('Admin Panel | JepPrep', 'Administrative dashboard for user management, analytics, and system monitoring.');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAnalytics(); }, []);

  const loadAnalytics = async () => {
    try {
      const res = await adminAPI.analytics();
      setData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="text-gray-400 text-center py-12">Loading analytics...</div>;
  if (!data) return null;

  const StatCard = ({ label, value, icon: Icon, color = 'text-[hsl(var(--brand-gold-500))]', filterKey }) => (
    <Card
      className={`bg-[hsl(var(--secondary))] border-[hsl(var(--border))] ${filterKey ? 'cursor-pointer hover:border-[hsl(var(--brand-gold-500))]/50 transition-all' : ''}`}
      onClick={() => filterKey && onFilterUsers?.(filterKey)}
      data-testid={filterKey ? `analytics-filter-${filterKey}` : undefined}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-400 text-sm">{label}</span>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <p className="text-white text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">Platform Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={data.users.total} icon={Users} filterKey="all" />
        <StatCard label="Signups Today" value={data.users.signups_today} icon={TrendingUp} color="text-emerald-400" filterKey="today" />
        <StatCard label="This Week" value={data.users.signups_week} icon={TrendingUp} color="text-cyan-400" filterKey="week" />
        <StatCard label="This Month" value={data.users.signups_month} icon={TrendingUp} color="text-violet-400" filterKey="month" />
      </div>

      <h2 className="text-xl font-bold text-white">Content</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Boards" value={data.content.total_boards} icon={LayoutGrid} />
        <StatCard label="Community Boards" value={data.content.community_boards} icon={Swords} color="text-emerald-400" />
        <StatCard label="Featured Boards" value={data.content.featured_boards} icon={Star} color="text-amber-400" />
        <StatCard label="Disabled Users" value={data.users.disabled} icon={Ban} color="text-red-400" />
      </div>

      <h2 className="text-xl font-bold text-white">Game Activity</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Sprint Sessions" value={data.activity.sprint_sessions} icon={Gamepad2} color="text-cyan-400" />
        <StatCard label="Daily Challenges" value={data.activity.daily_challenge_attempts} icon={Trophy} color="text-amber-400" />
        <StatCard label="Community Plays" value={data.activity.community_plays} icon={Swords} color="text-emerald-400" />
        <StatCard label="Category Sessions" value={data.activity.daily_categories_sessions} icon={BarChart3} color="text-violet-400" />
      </div>
    </div>
  );
}

// ==================== USERS TAB ====================
function UsersTab({ signupPeriod }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => { setPage(1); }, [signupPeriod]);
  useEffect(() => { loadUsers(); }, [page, sortBy, sortOrder, signupPeriod]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15, sort_by: sortBy, sort_order: sortOrder };
      if (search) params.search = search;
      if (signupPeriod) params.signup_period = signupPeriod;
      const res = await adminAPI.listUsers(params);
      setUsers(res.data.users);
      setTotalPages(res.data.pages);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadUsers(); };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 text-gray-600" />;
    return sortOrder === 'desc' ? <ArrowDown className="w-3 h-3 text-[hsl(var(--brand-gold-500))]" /> : <ArrowUp className="w-3 h-3 text-[hsl(var(--brand-gold-500))]" />;
  };

  const periodLabels = { all: 'All Users', today: 'Signups Today', week: 'This Week', month: 'This Month' };

  const toggleDisable = async (user) => {
    try {
      if (user.disabled) {
        await adminAPI.enableUser(user.id);
        toast.success(`${user.display_name} enabled`);
      } else {
        await adminAPI.disableUser(user.id);
        toast.success(`${user.display_name} disabled`);
      }
      loadUsers();
      if (selectedUser?.id === user.id) setSelectedUser(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const handleResetPassword = async (userId) => {
    if (!resetPw || resetPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await adminAPI.resetPassword(userId, resetPw);
      toast.success('Password reset');
      setResetPw('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Permanently delete "${user.display_name || user.email}"? This will remove the user and ALL their data (boards, sessions, achievements, etc.). This cannot be undone.`)) return;
    try {
      await adminAPI.deleteUser(user.id);
      toast.success(`User "${user.display_name || user.email}" deleted`);
      if (selectedUser?.id === user.id) setSelectedUser(null);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never';

  return (
    <div className="space-y-4">
      {signupPeriod && (
        <div className="flex items-center gap-2">
          <Badge className="bg-[hsl(var(--brand-gold-500))]/20 text-[hsl(var(--brand-gold-500))] border-[hsl(var(--brand-gold-500))]/30">
            Filtered: {periodLabels[signupPeriod] || signupPeriod}
          </Badge>
        </div>
      )}

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-white"
            data-testid="admin-user-search"
          />
        </div>
        <Button type="submit" className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]">Search</Button>
      </form>

      {/* Sort controls */}
      <div className="flex items-center gap-3 text-xs">
        <span className="text-gray-500">Sort by:</span>
        <button
          onClick={() => toggleSort('created_at')}
          className={`flex items-center gap-1 px-2 py-1 rounded ${sortBy === 'created_at' ? 'text-[hsl(var(--brand-gold-500))] bg-[hsl(var(--brand-gold-500))]/10' : 'text-gray-400 hover:text-white'}`}
          data-testid="sort-join-date"
        >
          Join Date <SortIcon field="created_at" />
        </button>
        <button
          onClick={() => toggleSort('last_login')}
          className={`flex items-center gap-1 px-2 py-1 rounded ${sortBy === 'last_login' ? 'text-[hsl(var(--brand-gold-500))] bg-[hsl(var(--brand-gold-500))]/10' : 'text-gray-400 hover:text-white'}`}
          data-testid="sort-last-login"
        >
          Last Login <SortIcon field="last_login" />
        </button>
      </div>

      {loading ? (
        <div className="text-gray-400 text-center py-8">Loading...</div>
      ) : (
        <div className="space-y-2">
          {users.map(user => (
            <Card key={user.id} className={`bg-[hsl(var(--secondary))] border-[hsl(var(--border))] ${user.disabled ? 'opacity-60' : ''}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-full" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[hsl(var(--brand-blue-800))]" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium text-sm truncate" data-testid="user-name">{user.display_name || 'No name'}</p>
                      {user.role === 'admin' && <Badge className="bg-violet-500/20 text-violet-400 text-[10px]">Admin</Badge>}
                      {user.disabled && <Badge className="bg-red-500/20 text-red-400 text-[10px]">Disabled</Badge>}
                    </div>
                    <p className="text-gray-500 text-xs truncate">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="hidden sm:flex flex-col items-end text-xs">
                    <span className="text-gray-500" data-testid="user-join-date">Joined: {formatDate(user.created_at)}</span>
                    <span className="text-gray-600" data-testid="user-last-login">Login: {formatDateTime(user.last_login)}</span>
                  </div>
                  {user.role !== 'admin' && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)} className="text-gray-400 hover:text-white" data-testid={`user-details-${user.id}`}>
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleDisable(user)} className={user.disabled ? 'text-emerald-400 hover:text-emerald-300' : 'text-red-400 hover:text-red-300'} data-testid={`user-toggle-${user.id}`}>
                        {user.disabled ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(user)} className="text-red-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`user-delete-${user.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
              {selectedUser?.id === user.id && (
                <div className="px-4 pb-4 border-t border-[hsl(var(--border))] pt-3">
                  <div className="flex items-center gap-2">
                    <Input
                      type="password"
                      placeholder="New password (min 6 chars)"
                      value={resetPw}
                      onChange={(e) => setResetPw(e.target.value)}
                      className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white flex-1"
                    />
                    <Button size="sm" onClick={() => handleResetPassword(user.id)} className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]">
                      Reset
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-[hsl(var(--border))] text-gray-300">Prev</Button>
          <span className="flex items-center px-4 text-gray-400 text-sm">Page {page} / {totalPages}</span>
          <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-[hsl(var(--border))] text-gray-300">Next</Button>
        </div>
      )}
    </div>
  );
}

// ==================== COMMUNITY MODERATION TAB ====================
function CommunityTab() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { loadBoards(); }, [page]);

  const loadBoards = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listCommunityBoards({ page, limit: 15 });
      setBoards(res.data.boards);
      setTotalPages(res.data.pages);
    } catch { toast.error('Failed to load boards'); }
    finally { setLoading(false); }
  };

  const toggleFeature = async (board) => {
    try {
      if (board.featured) {
        await adminAPI.unfeatureBoard(board.id);
        toast.success('Board unfeatured');
      } else {
        await adminAPI.featureBoard(board.id);
        toast.success('Board featured');
      }
      loadBoards();
    } catch { toast.error('Action failed'); }
  };

  const removeBoard = async (board) => {
    if (!window.confirm(`Remove "${board.title}" from community?`)) return;
    try {
      await adminAPI.removeCommunityBoard(board.id);
      toast.success('Board removed');
      loadBoards();
    } catch { toast.error('Remove failed'); }
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="text-gray-400 text-center py-8">Loading...</div>
      ) : boards.length > 0 ? (
        <div className="space-y-2">
          {boards.map(board => (
            <Card key={board.id} className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-white font-medium text-sm truncate" data-testid="board-mod-title">{board.title}</p>
                    {board.featured && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Featured</Badge>}
                    {board.community_challenge && <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Challenge</Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>by {board.created_by_name || 'Anonymous'}</span>
                    <span>{board.difficulty || 'Medium'}</span>
                    <span>{board.total_plays || 0} plays</span>
                    {board.challenge_score && <span className="text-[hsl(var(--brand-gold-500))]">${board.challenge_score.toLocaleString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => toggleFeature(board)} className={board.featured ? 'text-amber-400' : 'text-gray-400 hover:text-amber-400'} data-testid={`feature-toggle-${board.id}`}>
                    {board.featured ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeBoard(board)} className="text-red-400 hover:text-red-300" data-testid={`remove-board-${board.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-gray-400 text-center py-12">No community boards yet</div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-[hsl(var(--border))] text-gray-300">Prev</Button>
          <span className="flex items-center px-4 text-gray-400 text-sm">Page {page} / {totalPages}</span>
          <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-[hsl(var(--border))] text-gray-300">Next</Button>
        </div>
      )}
    </div>
  );
}

// ==================== SYSTEM HEALTH TAB ====================
function HealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadHealth(); }, []);

  const loadHealth = async () => {
    try {
      const res = await adminAPI.systemHealth();
      setData(res.data);
    } catch { toast.error('Failed to load health data'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="text-gray-400 text-center py-12">Loading...</div>;
  if (!data) return null;

  const HealthRow = ({ label, value, warn }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-[hsl(var(--border))] last:border-0">
      <span className="text-gray-400 text-sm">{label}</span>
      <span className={`font-medium text-sm ${warn ? 'text-red-400' : 'text-white'}`}>{typeof value === 'number' ? value.toLocaleString() : value}</span>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-400" />
            System Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4">
            <div className={`w-3 h-3 rounded-full ${data.status === 'healthy' ? 'bg-emerald-500' : 'bg-red-500'}`} />
            <span className={`font-medium ${data.status === 'healthy' ? 'text-emerald-400' : 'text-red-400'}`}>
              {data.status === 'healthy' ? 'All Systems Operational' : 'Issues Detected'}
            </span>
          </div>
          <HealthRow label="Last Check" value={new Date(data.timestamp).toLocaleString()} />
        </CardContent>
      </Card>

      <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-white text-lg">AI Generation</CardTitle>
        </CardHeader>
        <CardContent>
          <HealthRow label="Boards Generated (24h)" value={data.ai_generation.boards_last_24h} />
          <HealthRow label="Empty/Failed Boards" value={data.ai_generation.empty_boards} warn={data.ai_generation.empty_boards > 0} />
        </CardContent>
      </Card>

      <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-white text-lg">Database Collections</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8">
            <HealthRow label="Users" value={data.database.users} />
            <HealthRow label="Boards" value={data.database.boards} />
            <HealthRow label="Game Sessions" value={data.database.sessions} />
            <HealthRow label="Sprint Sessions" value={data.database.sprint_sessions} />
            <HealthRow label="Clues" value={data.database.clues} />
            <HealthRow label="Community Plays" value={data.database.community_plays} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== ADMIN LOGIN ====================
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onLogin(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <Card className="w-full max-w-sm bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <img src={LOGO_URL} alt="JepPrep" className="h-16 mx-auto mb-4" />
            <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 mb-3">
              <Shield className="w-3 h-3 mr-1" />
              Admin Panel
            </Badge>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              placeholder="Admin email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white"
              data-testid="admin-email-input"
              required
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white"
              data-testid="admin-password-input"
              required
            />
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white"
              data-testid="admin-login-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MAIN ADMIN PAGE ====================
export default function AdminPanel() {
  const { user, isAuthenticated, login } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [signupPeriod, setSignupPeriod] = useState(null);

  const handleFilterUsers = (period) => {
    setSignupPeriod(period);
    setActiveTab('users');
  };

  const handleAdminLogin = async (email, password) => {
    const result = await login(email, password);
    // After login, if user is not admin, the component will re-render and redirect
  };

  // Not logged in → show admin login form
  if (!isAuthenticated || !user) {
    return <AdminLogin onLogin={handleAdminLogin} />;
  }

  // Logged in but not admin → show access denied
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <Card className="w-full max-w-sm bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-8 text-center">
            <Ban className="w-12 h-12 mx-auto text-red-400 mb-3" />
            <p className="text-white font-bold text-lg mb-2">Access Denied</p>
            <p className="text-gray-400 text-sm mb-4">Admin privileges required.</p>
            <Link to="/dashboard">
              <Button variant="outline" className="border-[hsl(var(--border))] text-gray-300">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
              <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30">
                <Shield className="w-3 h-3 mr-1" />
                Admin
              </Badge>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-8 bg-[hsl(var(--secondary))] rounded-lg p-1 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); if (tab.id !== 'users') setSignupPeriod(null); }}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]'
                  : 'text-gray-400 hover:text-white hover:bg-[hsl(var(--accent))]'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.id === 'health' ? 'Health' : tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'analytics' && <AnalyticsTab onFilterUsers={handleFilterUsers} />}
        {activeTab === 'users' && <UsersTab signupPeriod={signupPeriod} />}
        {activeTab === 'community' && <CommunityTab />}
        {activeTab === 'blog' && <BlogTab />}
        {activeTab === 'email' && <EmailTab />}
        {activeTab === 'health' && <HealthTab />}
      </main>
    </div>
  );
}
